import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface KeyboardShortcutHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  { keys: ['G', 'D'], label: 'Go to Dashboard' },
  { keys: ['G', 'L'], label: 'Go to Leads' },
  { keys: ['G', 'B'], label: 'Go to Bookings' },
  { keys: ['G', 'W'], label: 'Go to Websites' },
  { keys: ['G', 'R'], label: 'Go to Reviews' },
  { keys: ['G', 'A'], label: 'Go to Analytics' },
  { keys: ['G', 'S'], label: 'Go to Search Keywords' },
  { keys: ['G', 'P'], label: 'Go to Content Plan' },
  { keys: ['G', 'N'], label: 'Go to Notifications' },
  { keys: ['\u2318', 'K'], label: 'Open search' },
  { keys: ['?'], label: 'Show this help' },
];

const Kbd: React.FC<{ children: string }> = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-zinc-100 border border-zinc-200 rounded text-xs font-mono font-medium text-primary">
    {children}
  </kbd>
);

const KeyboardShortcutHelp: React.FC<KeyboardShortcutHelpProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-xl shadow-2xl border border-zinc-200 p-6 max-w-md w-full pointer-events-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-display font-semibold text-primary">Keyboard Shortcuts</h2>
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2">
                {shortcuts.map((shortcut) => (
                  <div key={shortcut.label} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted">{shortcut.label}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-xs text-zinc-400 mx-0.5">then</span>}
                          <Kbd>{key}</Kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-zinc-400">
                Press <Kbd>G</Kbd> then a letter within 500ms to navigate.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default KeyboardShortcutHelp;
